<template>
  <!-- this should really be a view - will refactor -->
  <main class="tgbProducerDashboard">
    <h1 class="tgb-sr-only">Producer Dashboard</h1>

    <!-- should we abstract the layout of the directory as well? -->
    <div
      class="producer-dashboard-body"
      :class="{ detailsViewOpen: productionDetailsView }"
    >
      <div class="producer-dashboard-body__list">
        <div class="producerDashboardMainActions">
          <tgb-tabs
            :active-tab="activeTab"
            @tab-clicked="onClickedTab"
          >
            <template v-slot:tabAfterLast>
              <li class="tabs__trailing-tab flex items-center content-end">
                <select
                  v-if="showTeamDropdown"
                  expanded
                  name="accounts"
                  placeholder="Select an account"
                  class="tgb-select-dropdown margin-right-sm"
                  v-model="owner"
                >
                  <option
                    :key="acc.id"
                    v-for="acc in owners"
                    :value="acc.id"
                  >
                    {{ acc.teamName || acc.displayName }}{{ isStaff ? ` - ${acc.id}` : '' }}
                  </option>
                </select>

                <tgb-button
                  variant="primary"
                  data-entity="producer-dashboard-createProduction"
                  @click="openCreateNewProductionModal()"
                >
                  <i class="far fa-plus"></i>
                  New Production
                </tgb-button>
              </li>
            </template>
          </tgb-tabs>
        </div>

        <!-- make ids match tab content ids for accessibility -->
        <TransitionGroup
          :name="tabAnimationClass"
          tag="div"
          class="producerDashboardContentTransition"
        >
          <div
            v-if="productionsView === 'all'"
            id="activeProductions"
            key="activeProductions"
            class="producerDashboardContent"
          >
            <section
              v-if="flaggedProductions.length > 0"
              class="producerDashboardSection"
            >
              <directory
                header="Starred"
                :activeItem="detailedProduction"
                :items="flaggedProductions"
                :page-size="flaggedIntPageSize"
                :selectedComponentView.sync="starredView"
                :show-subheader="currentlyShowingStarred > 0 && currentlyShowingStarred < flaggedProductions.length"
                :subheader="`Showing ${currentlyShowingStarred} of ${flaggedProductions.length}`"
                :totalCount="flaggedProductions.length"
                :totalVisibleCount.sync="currentlyShowingStarred"
                @dir-item-clicked="showProductionDetails"
                @dir-item-starred="starProduction"
                @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
                @on-dir-context-menu="onContextMenuActive"
              />
            </section>

            <section class="producerDashboardSection">
              <directory
                header="Productions"
                :activeItem="detailedProduction"
                :items="productions"
                :page-size="productions.length > pageSize ? pageSize : productions.length"
                :selectedComponentView.sync="allView"
                :show-subheader="currentlyShowingAll > 0 && currentlyShowingAll < totalProductions"
                :subheader="`Showing ${currentlyShowingAll} of ${totalProductions}`"
                :totalCount="totalProductions"
                :totalVisibleCount.sync="currentlyShowingAll"
                :infinite="true"
                @dir-item-clicked="showProductionDetails"
                @dir-item-starred="starProduction"
                @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
                @load-more="loadMore"
                @on-dir-context-menu="onContextMenuActive"
              >
                <template v-slot:noDataMessage>
                  <div class="producerNoProductionsMessage">
                    <p class="tgbTitleLg">Hmm, you haven't created any cool productions.</p>
                    <p>Tell your story with Tagboard Producer!</p>
                  </div>
                </template>
              </directory>
            </section>
          </div>

          <div
            v-if="productionsView === 'deleted'"
            key="deletedProductions"
            class="producerDashboardContent"
            id="deletedProductions"
          >
            <div v-if="loading" class="loading loading--inline margin-md">
              <i class="icon icon-spinner"></i>
            </div>

            <section v-else class="producerDashboardSection">
              <directory
                header="Deleted"
                :subheader="`Showing ${currentlyShowingDeleted} of ${deletedProductions.length}`"
                :show-subheader="currentlyShowingDeleted > 0 && currentlyShowingDeleted < deletedProductions.length"
                :items="deletedProductions"
                :totalVisibleCount.sync="currentlyShowingDeleted"
                :selectedComponentView.sync="deletedView"
                @dir-item-clicked="showProductionDetails"
                :activeItem="detailedProduction"
                :totalCount="deletedProductions.length"
                :page-size="50"
                @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
                @on-dir-context-menu="onContextMenuDeleted"
              >
                <template v-slot:noDataMessage>
                  <div class="producerNoProductionsMessage">
                    <p class="tgbTitleLg">You haven't deleted any productions.</p>
                    <p>Remember to regularly clean out unused productions.</p>
                  </div>
                </template>
              </directory>
            </section>
          </div>
        </TransitionGroup>
      </div>

      <Transition name="slide-fade">
        <div class="producer-dashboard-body__details" v-if="productionDetailsView">
          <directory-details
            :key="detailedProduction"
            :details="productionDetailsView"
          >
            <template v-slot:beforeTitle>
              <button
                type="button"
                class="tb-btn tb-btn-text tgb-directory-menu-controls"
                data-entity="producer-details-closeDetails"
                @click="showNoDetails({ e: $event, payload: productionDetailsView})"
              >
                <span class="fas fa-times"></span>
              </button>

              <button
                type="button"
                class="tb-btn tb-btn-text tgb-directory-menu-controls"
                data-entity="producer-details-clickContextMenu"
                @click="detailsContextMenu({ e: $event, payload: productionDetailsView})"
              >
                <span class="fas fa-ellipsis-h"></span>
              </button>
            </template>

            <template v-slot:actions>
              <div class="tgbButtonRow tgbButtonReverse">
                <tgb-button
                  v-if="productionsView === 'all'"
                  type="button"
                  variant="primary"
                  class="tgbEnterProduction"
                  data-entity="producer-details-openProduction"
                  @click="sendConduitEventLogs(productionDetailsView)"
                >
                  Enter Production
                </tgb-button>

                <tgb-button
                  v-else-if="productionsView === 'deleted'"
                  type="button"
                  variant="primary"
                  data-entity="producer-details-restoreProduction"
                  @click="onRestoreProduction(productionDetailsView, 'details_restore_production_button')"
                >
                  Restore Production
                </tgb-button>

                <tgb-button
                  variant="tertiary"
                  data-entity="producer-details-closeDetails"
                  @click="showNoDetails({ e: $event, payload: productionDetailsView})"
                >
                  Close Details
                </tgb-button>
              </div>
            </template>
          </directory-details>
        </div>
      </Transition>

      <tgb-context-menu
        v-if="showContextMenu"
        v-click-outside="hideContextMenu"
        :actions="currentContextMenu"
      >
        <template v-slot="action">
          <button
            class="tgbContextMenuButton"
            :data-entity="`producer-context-${action.key}`"
            @click="action.onClickFunc(action.item, action.trigger)"
          >
            <span
              v-if="action.icon"
              class="fas"
              :class="action.icon"
            />

            {{ action.text }}
          </button>
        </template>
      </tgb-context-menu>

      <form @submit.prevent="submitProduction" class="tgb-modal-form">
        <tgb-modal
          ref="createNewProductionModal"
          :headerText="(editProductionMode ? `Edit` : `New`) + ` Production`"
          @modal-close="clearNewProductionForm"
        >
          <div class="margin-top-lg margin-bottom-md">
            <label
              class="block tgb-label"
              for="productionTitle"
            >
              Title
            </label>

            <input
              id="productionTitle"
              type="text"
              name="title"
              placeholder="Production Title"
              v-model.lazy="productionForm.title"
              class="tgb-text-input"
              :class="{ critical: errors.title }"
              @blur="validateProductionTitle"
            />

            <p
              v-if="errors.title"
              class="formFieldError"
            >
              <span class="fas fa-exclamation-triangle" />
              Production title length must be between 4 and 100 characters.
            </p>
          </div>

          <tgb-tags
            class="margin-bottom-md"
            v-model="productionForm.tags"
            @invalid="toggleInvalidTags"
          />

          <label class="cursor-pointer">
            <input
              type="checkbox"
              class="tgb-sr-only"
              v-bind:value="productionForm.flagged"
              @click="productionForm.flagged = !productionForm.flagged"
            >

            <span
              class="fa-star"
              :class="productionForm.flagged ? 'fas' : 'far'"
            />

            <span class="tgb-label">
              Add to Starred
            </span>
          </label>

          <!-- Create Production Controls -->
          <template
            v-if="!editProductionMode"
            #footer
          >
            <div class="tgbButtonRow tgbButtonReverse">
              <tgb-button
                id="tgbNewProductionSubmitAndEnter"
                type="submit"
                variant="primary"
                :disabled="isFormInvalid"
                data-entity="producer-dashboard-createAndOpenProduction"
              >
                Create
              </tgb-button>

              <tgb-button
                type="reset"
                variant="tertiary"
                data-entity="producer-dashboard-cancelCreateProduction"
                @click="closeCreateNewProductionModal"
              >
                Cancel
              </tgb-button>
            </div>
          </template>

          <!-- Edit Production Controls -->
          <template
            v-else
            #footer
          >
            <div class="tgbButtonRow tgbButtonReverse">
              <tgb-button
                type="submit"
                variant="primary"
                :disabled="isFormInvalid"
                data-entity="producer-dashboard-saveProductionDetails"
              >
                Save Changes
              </tgb-button>

              <tgb-button
                type="reset"
                variant="tertiary"
                data-entity="producer-dashboard-cancelEditProductionDetails"
                @click="closeCreateNewProductionModal"
              >
                Cancel
              </tgb-button>
            </div>
          </template>
        </tgb-modal>
      </form>
    </div>
  </main>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { sortBy, pick } from 'lodash-es';

import ConduitEventsService from '../../services/conduitEventsService';

import DirectoryDetails from '../directory/DirectoryDetails.vue';
import Directory from '../directory/Directory.vue';
import TgbTabs from '../tabs/TgbTabs.vue';
import TgbModal from '../modal/TgbModal.vue';
import TgbContextMenu from '../context-menu/TgbContextMenu.vue';
import TgbTags from '../tags/TgbTags.vue';
import TgbButton from '../button/TgbButton.vue';

import GraphicsService from '../../services/graphics';

import toast from '../toast/toast';

const LocalStorage = {
  get(key, fallback = null) {
    return window.localStorage.getItem(key) || fallback;
  },

  set(key, value) {
    window.localStorage.setItem(key, value);
  },
};

export default {
  name: 'ProducerDashboard',

  components: {
    DirectoryDetails,
    Directory,
    TgbTabs,
    TgbModal,
    TgbContextMenu,
    TgbTags,
    TgbButton,
  },

  data() {
    return {
      allView: 'tgb-directory-table',
      starredView: 'tgb-directory-grid',
      deletedView: 'tgb-directory-table',
      searchProductionsString: '',
      productionsView: 'all',
      activeTab: 0,
      tabAnimationClass: 'productions-slide-right',
      productionDetailsView: null,
      currentlyShowingStarred: 0,
      currentlyShowingAll: 0,
      currentlyShowingDeleted: 0,
      contextMenuActive: [],
      contextMenuDeleted: [],
      editProductionMode: false,
      errors: {},
      productionForm: {
        title: '',
        tags: [],
        flagged: false,
      },
      showContextMenu: false,
      detailedProduction: null,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      accounts: (state) => state.user.accounts,
      productions: (state) => state.production.productions,
      flaggedProductions: (state) => state.production.flaggedProductions,
      deletedProductions: (state) => state.production.deletedProductions,
      totalProductions: (state) => state.production.total,
      pageSize: (state) => state.production.limit,
      loading: (state) => state.production.loading,
      hasPlan: (state) => state.user.hasPlan,
    }),

    ...mapGetters([
      'isStaff',
    ]),

    showTeamDropdown() {
      return this.owners.length > 1;
    },

    owners() {
      const { owners } = this.accounts;

      const teams = sortBy(Object.values(owners || {}), (owner) => {
        const name = owner.teamName?.toLowerCase() || owner.displayName?.toLowerCase() || '';
        return name;
      });

      return [
        this.hasPlan ? this.user : null,
        ...teams,
      ].filter(Boolean);
    },

    owner: {
      get() {
        return this.$store.state.production.owner;
      },

      set(owner) {
        const ownerIds = this.owners.map((acc) => acc.id);
        const isTeamMember = ownerIds.some((id) => id === owner);

        // Make sure user is actually on the team
        if (!isTeamMember) {
          if (ownerIds.includes(+this.owner)) {
            // Just set back to current value (looks pretty funny, but works frfr).
            // This allows us to reset the route param back to something valid.
            this.owner = this.owner; // eslint-disable-line no-self-assign
          }

          return;
        }

        // Set owner in store
        this.$store.dispatch('setOwner', owner);

        // Update route param if necessary
        if (owner && +this.$route.params.owner !== owner) {
          this.$router.replace({
            name: 'productions',
            params: {
              tab: this.$route.params.tab || 'all',
              owner,
            },
          });
        }

        // Put it in local storage to remember for later
        LocalStorage.set('selectedTeam', owner);

        // Clear current state so user knows we are loading stuff
        this.clearProductions();

        // Refetch production list(s)
        this.loadData();
      },
    },

    flaggedIntPageSize() {
      return this.flaggedProductions.length > 10 ? 10 : this.flaggedProductions.length;
    },

    detailsContextMenu() {
      return this.productionsView === 'deleted' ? this.onContextMenuDeleted : this.onContextMenuActive;
    },

    currentContextMenu() {
      return this.productionsView === 'deleted' ? this.contextMenuDeleted : this.contextMenuActive;
    },

    isFormInvalid() {
      return !this.productionForm.title || !!Object.values(this.errors).filter(Boolean).length;
    },

    production() {
      return this.$store.state.production.production;
    },
  },

  methods: {
    ...mapActions([
      'incrementPage',
      'checkConnectedScreens',
      'getConnectedUsers',
      'getDeletedProductions',
      'getProductions',
      'getStarredProductions',
      'deleteProduction',
      'restoreProduction',
      'getUserAccounts',
      'getUserPlan',
      'clearProductions',
    ]),

    validateProductionTitle() {
      const { title } = this.productionForm;

      if (title && title.length < 4 || title.length > 100) {
        this.$set(this.errors, 'title', true);
      } else {
        this.$set(this.errors, 'title', false);
      }
    },

    loadData() {
      this.getProductions()
        .then((data) => {
          this.checkConnectedScreens();
          this.getConnectedUsers();

          const view = LocalStorage.get('productionsAllView', this.allView);

          ConduitEventsService.track(
            'producer.loaded.productions-list',
            {
              allProductionsCount: data.total,
              view: view === 'tgb-directory-grid' ? 'card' : 'table',
            },
            {
              ownerId: this.$store.state.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
          );
        }).catch((err) => {
          console.error('Failed to load productions:', err);

          toast.makeToast({
            heading: 'Error',
            icon: 'error',
            loaderBg: 'red',
            text: 'Failed to load productions.',
          });
        });

      this.getStarredProductions()
        .then((productions) => {
          const view = LocalStorage.get('productionsStarredView', this.starredView);

          // Developer NOTE: Seems redundant to have this AND the above event when they both fire together
          ConduitEventsService.track(
            'producer.loaded.flagged-productions-list',
            {
              starredProductionsCount: productions.length,
              view: view === 'tgb-directory-grid' ? 'card' : 'table',
            },
            {
              ownerId: this.$store.state.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
          );
        }).catch((err) => {
          console.error('Failed to load starred productions:', err);

          toast.makeToast({
            heading: 'Error',
            icon: 'error',
            loaderBg: 'red',
            text: 'Failed to load starred productions.',
          });
        });

      if (this.$route.params.tab === 'deleted') {
        this.productionsView = 'deleted';
        this.activeTab = 1;
        this.loadDeletedProductions();
      }
    },

    toggleInvalidTags(v) {
      if (v) {
        this.$set(this.errors, 'tags', true);
      } else {
        this.$set(this.errors, 'tags', false);
      }
    },

    sendConduitEventLogs(production) {
      ConduitEventsService.track(
          'producer.launched.production',
          {
            production_id: production._id,
            title: production.title,
            tags: production.tags,
            starred: production.flagged,
            trigger: 'details_panel',
            method: 'sendConduitEventLogs',
          },
          {
            ownerId: production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );

      this.$router.push({
        name: 'production',
        params: { production_id: production._id },
      });
    },

    async createNewProduction() {
      this.$store.dispatch('startLoader');

      await this.$store.dispatch('createProduction', this.productionForm).then((id) => {
        this.successToast('Production created successfully');

        this.$router.push({
          name: 'production',
          params: { production_id: id },
        });

        this.$store.dispatch('stopLoader');

        ConduitEventsService.track(
          'producer.created.production',
          {
            production_id: id,
            tags: this.productionForm.tags,
            starred: this.productionForm.flagged,
            title: this.productionForm.title,
          },
          {
            ownerId: this.$store.state.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
        );
      }).catch((err) => {
        console.error('Error creating production:', err);

        this.errorToast(
          'There was an error creating your production. Please try again.',
        );
      }).finally(() => {
        this.$store.dispatch('stopLoader');
      });
    },

    async updateProduction() {
      this.$store.dispatch('startLoader');

      const { flagged } = this.productionForm;
      const payload = pick(this.productionForm, ['title', 'tags']);

      // Only handling flagging if the value has changed.
      // This improves response time and lowers api calls.
      const production = this.$store.state.production.productions.find((p) => p._id === this.selectedProductionId); // eslint-disable-line max-len
      if (flagged !== production.flagged) {
        await this.starProduction(production);
      }

      await this.$store.dispatch('updateProduction', {
        id: this.selectedProductionId,
        payload,
      }).then(() => {
        this.successToast('Production saved!');
        this.closeCreateNewProductionModal();
      }).catch((err) => {
        console.error('Error updating production:', err);
        this.errorToast('There was an error creating your production. Please try again.');
      });

      this.$store.dispatch('stopLoader');
    },

    submitProduction() {
      if (this.editProductionMode) {
        this.updateProduction();
      } else {
        this.createNewProduction();
      }
    },

    openCreateNewProductionModal() {
      this.$refs.createNewProductionModal.open();
      this.editProductionMode = false;
    },

    closeCreateNewProductionModal() {
      this.$refs.createNewProductionModal.close();
    },

    openEditProductionsModal(production, trigger) {
      this.editProductionMode = true;
      this.$refs.createNewProductionModal.open();

      const {
        owner,
        title,
        tags,
        flagged,
        _id,
      } = { ...production };

      this.selectedProductionId = _id;
      this.productionForm = {
        owner,
        title,
        tags,
        flagged,
        trigger,
      };
    },

    animationDirectionSwitch(current, next) {
      if (current > next) {
        this.tabAnimationClass = 'productions-slide-left';
      } else {
        this.tabAnimationClass = 'productions-slide-right';
      }
    },

    onClickedTab({ tab, index }) {
      this.animationDirectionSwitch(this.activeTab, index);
      this.productionsView = tab.contentId;
      this.activeTab = index;

      this.showNoDetails({
        payload: {
          ...this.productionDetailsView,
          trigger: 'productions_list_tabs',
        },
      });

      this.$router.push({
        name: 'productions',
        params: { tab: tab.contentId },
      });

      // Only load deleted productions if user navigated to that tab
      if (tab.contentId === 'deleted' && !this.$store.state.production.loadedDeletedProductions) {
        this.loadDeletedProductions();
      }
    },

    loadDeletedProductions() {
      this.getDeletedProductions()
        .then((data) => {
          const view = LocalStorage.get('productionsDeletedView', this.deletedView);

          ConduitEventsService.track(
            'producer.loaded.deleted-productions-list',
            {
              deletedProductionsCount: data.length,
              view: view === 'tgb-directory-grid' ? 'card' : 'table',
            },
            {
              ownerId: this.$store.state.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
          );
        }).catch((err) => {
          console.error('Failed to load deleted productions:', err);

          toast.makeToast({
            heading: 'Error',
            icon: 'error',
            loaderBg: 'red',
            text: 'Failed to load deleted productions.',
          });
        });
    },

    starProduction(production) {
      return this.$store.dispatch('starProduction', production._id).catch(() => {
        this.errorToast('There was an error starring your production.');
      });
    },

    showProductionDetails(production) {
      this.productionDetailsView = production;
      this.detailedProduction = production._id;
    },

    showNoDetails({
      e,
      payload,
    }) {
      this.productionDetailsView = null;
      this.detailedProduction = null;

      ConduitEventsService.track(
        'producer.closed.details-panel',
        {
          production_id: payload._id,
          tags: payload.tags,
          starred: payload.flagged,
          trigger: payload.trigger || (e ? e.currentTarget.dataset.tracking : null),
        },
        {
          ownerId: payload.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },

    async loadMore() {
      this.incrementPage();

      await this.getProductions();

      // Fetch screens + connected users for the new productions we load
      this.checkConnectedScreens();
      this.getConnectedUsers();
    },

    onDeleteProduction(item, trigger) {
      this.showContextMenu = false;

      this.deleteProduction({
        production: item,
        trigger,
      });

      this.showNoDetails({
        payload: {
          ...item,
          trigger,
        },
      });
    },

    onRestoreProduction(item, trigger) {
      this.restoreProduction({
        production: item,
        trigger,
      });

      this.showNoDetails({
        payload: {
          ...item,
          trigger,
        },
      });
    },

    async onDuplicateProduction(item) {
      this.$store.dispatch('startLoader');

      this.productionForm.title = `${item.title} (Copy)`.substr(0, 100);
      this.productionForm.owner = item.owner;
      this.productionForm.tags = item.tags;
      this.productionForm.flagged = item.flagged;
      this.productionForm.backgroundType = item.backgroundType;
      this.productionForm.backgroundColor = item.backgroundColor;
      this.productionForm.backgroundImage = item.backgroundImage;
      this.productionForm.backgroundVideo = item.backgroundVideo;
      this.showContextMenu = false;

      try {
        const { timeline } = await this.$store.dispatch('getProduction', item._id);
        this.productionForm.timeline = timeline;

        const id = await this.$store.dispatch('createProduction', this.productionForm);

        // Refetch data to update lists
        this.getProductions();
        if (this.productionForm.flagged) {
          this.getStarredProductions();
        }

        const owner = { owner: item.owner };
        await GraphicsService.duplicateSavedGraphics(item._id, id);

        this.successToast('Production duplicated successfully');
      } catch (err) {
        this.errorToast(
          'There was an error duplicating the production. Please try again.',
        );
      } finally {
        this.$store.dispatch('stopLoader');
      }
    },

    hideContextMenu() {
      this.showContextMenu = false;
    },

    onContextMenuActive({
      e,
      payload,
    }) {
      this.contextMenuActive = [
        {
          key: 'openProduction',
          text: 'Open',
          item: payload,
          icon: 'fa-sign-in-alt',
          onClickFunc: this.enterProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
        {
          key: 'editDetails',
          text: 'Edit Details',
          item: payload,
          icon: 'fa-edit',
          onClickFunc: this.openEditProductionsModal,
          trigger: e.currentTarget.dataset.tracking,
        },
        {
          key: 'duplicateProduction',
          text: 'Duplicate',
          item: payload,
          icon: 'fa-copy',
          onClickFunc: this.onDuplicateProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
        {
          key: 'deleteProduction',
          text: 'Delete',
          item: payload,
          icon: 'fa-trash',
          onClickFunc: this.onDeleteProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
      ];

      this.showContextMenu = true;

      ConduitEventsService.track(
          'producer.triggered.context-menu',
          {
            production_id: payload._id,
            title: payload.title,
            tags: payload.tags,
            starred: payload.flagged,
            trigger: e.currentTarget.dataset.tracking,
          },
          {
            ownerId: payload.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    onContextMenuDeleted({ e, payload }) {
      this.contextMenuDeleted = [
        {
          key: 'restoreProduction',
          text: 'Restore',
          item: payload,
          icon: 'fa-reply-all',
          onClickFunc: this.onRestoreProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
      ];

      this.showContextMenu = true;
    },

    onDropDownMenuItemClicked(payload) {
      const {
        callbackName,
        item,
        trigger,
      } = payload;

      this[callbackName](item, trigger);
    },

    enterProduction(production) {
      this.$router.push({
        name: 'production',
        params: { production_id: production._id },
      });

      ConduitEventsService.track(
          'producer.launched.production',
          {
            production_id: production._id,
            title: production.title,
            tags: production.tags,
            starred: production.flagged,
            trigger: 'details_panel',
            method: 'enterProduction',
          },
          {
            ownerId: production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    clearNewProductionForm() {
      this.productionForm = {
        title: '',
        tags: [],
        flagged: false,
      };
    },

    setCancelEditProductionValue(production) {
      this.cancelProductionValues = production;
    },
  },

  watch: {
    $route(to, from) {
      // If user updates route manually without using dropdown,
      // then make sure state keeps up.
      // Probably used mostly by staff, but still annoying to not see things update after updating the hash route.
      if (from.params.owner !== to.params.owner && +this.owner !== +to.params.owner) {
        this.owner = +to.params.owner;
      }
    },

    allView(view) {
      LocalStorage.set('productionsAllView', view);
    },

    starredView(view) {
      LocalStorage.set('productionsStarredView', view);
    },

    deletedView(view) {
      LocalStorage.set('productionsDeletedView', view);
    },
  },

  mounted() {
    this.allView = LocalStorage.get('productionsAllView', this.allView);
    this.starredView = LocalStorage.get('productionsStarredView', this.starredView);
    this.deletedView = LocalStorage.get('productionsDeletedView', this.deletedView);

    if (this.owner) {
      this.clearProductions();
      this.loadData();
    }
  },

  async created() {
    const [accounts, hasPlan] = await Promise.all([
      this.getUserAccounts(),
      this.getUserPlan(),
    ]).catch((err) => {
      console.error('Failed to load user details and permissions:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load user details and permissions.',
      });

      return [null];
    });

    if (!accounts) { return; }

    const { owners } = accounts;

    const ownerIds = [
      hasPlan ? this.user.id : null,
      ...(owners ? Object.keys(owners).map((id) => +id) : []),
    ].filter(Boolean);

    let owner;

    // Set owner based on route param, but fallback to first one in list
    if (this.$route.params.owner) {
      // Make sure user is actually on the team first
      if (ownerIds.some((id) => id === +this.$route.params.owner)) {
        owner = +this.$route.params.owner;
      }
    } else {
      // No route param, so check local storage for a default team.
      // Fallback to first account in list of owners.
      owner = +LocalStorage.get('selectedTeam', ownerIds[0]);
    }

    // Check that owner exists, and is still valid.
    if (!owner || !ownerIds.includes(owner)) {
      // Default to first one, and update route param to reflect state
      [owner] = ownerIds;
    }

    // Setting owner will make sure route param is updated, local storage set, and store is clean.
    // Then it will fetch productions for the current owner.
    if (this.owner !== owner) {
      this.owner = owner;
    }
  },
};
</script>

<style lang="scss">
//reset some bootstrap stuff just here. Delete when we remove the BS dependency
label {
  display: auto;
  max-width: auto;
  margin-bottom: auto;
  font-weight: inherit;
}

// styles that belong here
.producer-dashboard-body {
  &.detailsViewOpen {
    display: grid;
    grid-template-columns: auto 444px;
    grid-auto-rows: 100%;
  }

  height: calc(100vh - 60px);
}

.producer-dashboard-body__list {
  padding: 3.5rem 2.5rem;
  height: 100%;
  overflow: auto;
}

.producer-dashboard-body__details {
  height: 100%;
  width: 444px;
  overflow: auto;
  border-left: 1px solid #252525;
}

.producerDashboardSection {
  &:not(:last-child) {
    margin-bottom: 72px; // we need to talk about grid and spacing increments
  }
}

.producerDetailsVersion {
  flex-grow: 1;
}

.producerDashboardContentTransition {
  position: relative;
  overflow: visible;
}

.productions-slide-left-leave-active,
.productions-slide-left-enter-active,
.productions-slide-right-leave-active,
.productions-slide-right-enter-active {
  transition: 400ms;
  position: absolute;
  top: 0;
  width: 100%;
}

.productions-slide-right-leave-active {
  right: 36px;
}

.productions-slide-left-leave-active {
  left: 36px;
}

.productions-slide-right-enter,
.productions-slide-left-leave-to {
  transform: translate(100%, 0);
}

.productions-slide-right-leave-to,
.productions-slide-left-enter {
  transform: translate(-100%, 0);
}

// styles that eventually should be moved to a more specific place
.tgb-form-group {
  display: flex;

  .icon {
    position: absolute;
    width: 35px;
    height: 35px;
    text-align: center;
    font-size: 15px;
    line-height: 30px;
  }

  .tgb-input-icon {
    padding-left: 35px;
  }
}

.tgb-label {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
}

.tgbTitleLg {
  font-style: normal;
  font-weight: 900;
  font-size: 18px;
  line-height: 100%;
  word-break: break-all;
}

.tgb-input {
  display: block;
  width: 100%;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  color: #e1e1e1;
  background-color: var(--tgb-surface-default-color);
  border-radius: var(--tgb-border-radius-default);
  border: 1px solid transparent;
  outline: 0;
  box-shadow: none;
}

.tgbMetaLabel {
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #808689; //text primary inactive (not sure I like using inactive)
}

.tgbDirectoryBeforeTitle {
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
}

.tbgIconCheck::before, .tgb-icon-ellipsis::before {
  font-size: 16px;
  cursor: pointer;
}

.tbgIconChecked {
  color: #23CDF4; //todo: set to var based on primary
}

// todo: when we make a context menu items default component this should go there
.tgbContextMenuButton {
  .fas {
    margin-right: 8px;
  }
}

.tgbDirectoryMenuControls {
  font-size: 18px;
}

.tgb-btn-group {
  display: flex;
  gap: 12px; // there is a better place for this when we start making a global btn group
  align-items: center;
  justify-content: center;
}

//should be global
//utility classes should be global and more generated
.tgb-flex {
  display: flex;
}

.tgbNoWrap {
  white-space: nowrap;
}

.tgb-text-bold {
  font-weight: 700;
}

[aria-role="button"] {
  cursor: pointer;
}

.tgb-full-width {
  width: 100%
}

.modalContentTitle {
  color: #e1e1e1;
}

.tgb-modal-form {
  .tgbModal {
    width: 100%;
    max-width: 700px;
  }
}

.formFieldError {
  font-family: Barlow, sans-serif;;
  font-weight: 700;
  font-size: 14px;
  color: #FF9E98;
  margin-top: 12px;
}

.tgb-select-dropdown {
  min-width: 200px;
  padding: 10px 15px;
  line-height: 14px;
  font-size: 14px;
  border: 1px solid #141B20;
  background-color: #333; // to match old theme
  background-image: none;
  border-radius: 2px;

  &:focus {
    border-color: #23CDF4;
    box-shadow: 0px 0px 0px 4px rgba(43, 172, 202, 0.16);
    border-radius: 2px;
  }

  &.critical,
  &.critical:focus {
    border-color: #FF766D;
    box-shadow: 0px 0px 0px 4px rgba(255, 118, 109, 0.24);
    border-radius: 2px;
  }
}

.tgb-text-input,
.tgb-tags__input-container {
  display: block;
  width: 100%;
  background-color: #333; // to match old theme
  padding: 12px;
  border: 1px solid #141B20;
  margin-top: 10px;

  &:focus,
  &:focus-within {
    border-color: #23CDF4;
    box-shadow: 0px 0px 0px 4px rgba(43, 172, 202, 0.16);
    border-radius: 2px;
  }

  &.critical,
  &.critical:focus,
  &.critical:focus-within {
    border-color: #FF766D;
    box-shadow: 0px 0px 0px 4px rgba(255, 118, 109, 0.24);
    border-radius: 2px;
  }
}

.tgb-tags {
  display: flex;
  grid-gap: 8px;
  flex-wrap: wrap;
  align-items: center;

  &__tag {
    padding: 8px 12px;
    font-weight: 400;
    font-size: 13px;
    line-height: 150%;
    border-radius: 4px; // todo: check this radius as the other global things are 5px
    background: #253c42; // todo: this color is mapped to no theme color in the mockups
    color: #23CDF4; // todo: this color is mapped to no theme color in the mockups

    &::before {
      content: "#";
    }
  }
}

.tgb-tags__input-container {
  & > div {
    @extend .tgb-tags;
  }
}

.tgbButtonRow {
  display: flex;
  gap: 8px;

  &.tgbButtonReverse {
    flex-direction: row-reverse;
    margin-left: auto;
  }
}

.tgbModalFooter {
  margin-top: 40px;
}

.tgbEnterProduction {
  padding: 10px 15px;
  font-weight: 700;
  font-family: Barlow, sans-serif;
  font-style: normal;
  font-size: 14px;
  line-height: 14px;
  gap: 8px;
  cursor: pointer;
  color: #fff !important;
  border: #007F9D;
  border-radius: 2px;
  background-color: #007F9D;
  margin-left: 10px;

  &:hover {
    background: #109BB8;
    border-color: #109BB8;
  }

  &:active {
    background: #2BACCA;
    border-color: #2BACCA;
  }

  &:disabled {
    color: #333333;
    border-color: #6D7D85;
    background-color: #6D7D85;
  }
}

.producerNoProductionsMessage {
  padding-top: 100px;
  text-align: center;
}
</style>
